import type { LogOptions } from "./console-logging"
import {
  formatFlowErrorMessage,
  normalizeError,
  serializeError,
} from "./error-serializer"
import { addMonitorBreadcrumb } from "./monitor-breadcrumb"
import type { MonitorSpanOptions } from "./monitor-span"
import { withMonitorSpan } from "./monitor-span"
import { scrubValue } from "./sentry-privacy"
import { captureException, flushMonitor } from "./sentry-state"

type FlowLevel = NonNullable<LogOptions["level"]>

export interface FlowStepOptions {
  feature: string
  step: string
  category?: string
  data?: Record<string, unknown>
  level?: FlowLevel
}

export interface TrackedFailureOptions {
  error: unknown
  feature: string
  step: string
  tags?: Record<string, string>
  extra?: Record<string, unknown>
  flush?: boolean
}

export interface InstrumentedCallOptions {
  feature: string
  step: string
  spanName: string
  op: string
  attributes?: Record<string, string | number | boolean>
}

export function recordFlowStep({
  feature,
  step,
  category,
  data,
  level = "info",
}: FlowStepOptions) {
  addMonitorBreadcrumb({
    category: category ?? feature,
    level,
    message: step,
    data: scrubValue({
      feature,
      step,
      ...data,
    }) as Record<string, unknown>,
  })
}

export function recordFlowOutcome(
  feature: string,
  step: string,
  outcome: "success" | "fail",
  extra?: Record<string, unknown>
) {
  recordFlowStep({
    feature,
    step,
    data: { outcome, ...extra },
    level: outcome === "fail" ? "error" : "info",
  })
}

export function recordTrackedFailure({
  error,
  feature,
  step,
  tags,
  extra,
  flush = true,
}: TrackedFailureOptions) {
  const serialized = serializeError(error)
  const errorCode = serialized.errorCode ?? tags?.error_code ?? "unknown"

  recordFlowStep({
    feature,
    step,
    data: {
      ...extra,
      error_code: errorCode,
      error: serialized,
    },
    level: "error",
  })

  captureException(normalizeError(error), {
    message: formatFlowErrorMessage(feature, step, serialized),
    tags: {
      feature,
      step,
      error_code: errorCode,
      http_status:
        serialized.httpStatus != null
          ? String(serialized.httpStatus)
          : tags?.http_status ?? "none",
      ...(serialized.api ? { api: serialized.api } : {}),
      ...tags,
    },
    extra: {
      ...extra,
      error_code: errorCode,
      error: serialized,
    },
    contexts: {
      flow: {
        feature,
        step,
        error_code: errorCode,
        strategy: extra?.strategy,
        api: serialized.api,
      },
    },
    fingerprint: ["flow-failure", feature, step, errorCode],
  })

  if (flush) {
    void flushMonitor(2000)
  }
}

export async function runFlowSpan<T>(
  options: MonitorSpanOptions & {
    feature: string
    attributes?: Record<string, string | number | boolean>
  },
  callback: () => Promise<T> | T
): Promise<T> {
  const { feature, attributes, ...spanOptions } = options

  return withMonitorSpan(
    {
      ...spanOptions,
      attributes: {
        feature,
        ...attributes,
      },
    },
    callback
  )
}

export async function runInstrumentedCall<T>(
  options: InstrumentedCallOptions,
  callback: () => Promise<T>
): Promise<T> {
  recordFlowStep({
    feature: options.feature,
    step: options.step,
    data: options.attributes,
  })

  return runFlowSpan(
    {
      name: options.spanName,
      op: options.op,
      feature: options.feature,
      forceTransaction: true,
      attributes: options.attributes,
    },
    async () => {
      try {
        return await callback()
      } catch (error) {
        recordFlowStep({
          feature: options.feature,
          step: options.step,
          data: {
            ...options.attributes,
            error: serializeError(error),
          },
          level: "error",
        })
        throw error
      }
    }
  )
}
